import React, { useEffect, useState, useRef, useImperativeHandle } from 'react';
import './requirements.css';
import IntentBuyForm from './IntentBuyForm';
import IntentRentForm from './IntentRentForm';
import IntentSellForm from './IntentSellForm';
import IntentInvestForm from './IntentInvestForm';
import IntentOtherForm from './IntentOtherForm';
import ResetWarningModal from './components/ResetWarningModal';
import ConfirmModal from '../../../../components/ConfirmModal';

const INTENTS = [
    { value: 'buy', label: 'Buy' },
    { value: 'rent', label: 'Rent' },
    { value: 'sell', label: 'Sell' },
    { value: 'invest', label: 'Invest' },
    { value: 'other', label: 'Other' },
];

const CATEGORIES = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'land', label: 'Land' },
];

const normalizeData = (req = {}) => {
    if (req.basics || req.budget?.min !== undefined) {
        return {
            intent: req.intent || 'buy',
            listingType: req.listingType || 'sell',
            sellerListingIntent: req.sellerListingIntent || (req.listingType === 'rent' ? 'rent' : 'sale'),
            wantedIntent: req.wantedIntent || req.intent || 'unknown',
            category: req.category || 'residential',
            basics: req.basics || {},
            budget: req.budget || {},
            specifications: req.specifications || {},
            advanced: req.advanced || {},
            notes: req.notes || '',
        };
    }

    return {
        intent: req.intent || 'buy',
        listingType: req.listingType || 'sell',
        sellerListingIntent: req.sellerListingIntent || (req.listingType === 'rent' ? 'rent' : 'sale'),
        wantedIntent: req.wantedIntent || req.intent || 'unknown',
        category: req.category || 'residential',
        basics: {
            city: req.city,
            localities: req.localities,
            propertyTypes: req.propertyTypes || [],
            address: req.address,
        },
        budget: {
            min: req.budgetMin,
            max: req.budgetMax,
        },
        specifications: {
            areaMin: req.areaMin,
            areaMax: req.areaMax,
            areaUnit: req.areaUnit,
            bhkMin: req.bhkMin,
            bhkMax: req.bhkMax,
            possessionTimeline: req.possessionTimeline,
            furnishing: req.furnishing,
            moveInDate: req.moveInDate,
        },
        advanced: {
            financing: req.financing,
            purpose: req.purpose,
        },
        notes: req.notes || '',
    };
};

const contextKey = (intent, category) => `${intent || 'buy'}:${category || 'residential'}`;

const buildFlatMix = (form) => {
    const wantedIntent = form.wantedIntent || form.intent || 'unknown';
    const matchIntent = wantedIntent === 'rent' ? 'rent' : wantedIntent === 'buy' || wantedIntent === 'invest' ? 'sale' : 'unknown';
    const sellerListingIntent = form.sellerListingIntent || (form.listingType === 'rent' ? 'rent' : 'sale');
    return {
        ...form,
        intent: form.intent,
        listingType: form.listingType,
        wantedIntent,
        matchIntent,
        sellerListingIntent,
        category: form.category,
        city: form.basics?.city,
        localities: form.basics?.localities,
        pincode: form.basics?.pincode,
        address: form.basics?.address,
        budgetMin: form.budget?.min,
        budgetMax: form.budget?.max,
        propertyTypes: form.basics?.propertyTypes,
        updatedAt: new Date().toISOString(),
    };
};

const buildContextMap = (contexts) => {
    const map = {};
    Object.keys(contexts || {}).forEach((key) => {
        const [intent, category] = key.split(':');
        if (!intent || !category) return;
        map[intent] = map[intent] || {};
        map[intent][category] = buildFlatMix(contexts[key]);
    });
    return map;
};

const LeadRequirementsForm = React.forwardRef(({ lead, onSave, onDraftChange, onContextChange, onDirtyChange, loading }, ref) => {
    const [form, setForm] = useState(normalizeData(lead?.requirements));
    const [saveStatus, setSaveStatus] = useState('idle'); // idle, saved, saving, error
    const [isDirty, setIsDirty] = useState(false);
    const contextsRef = useRef({});
    const savedContextsRef = useRef({});
    const [showResetModal, setShowResetModal] = useState(false);
    const [pendingSwitch, setPendingSwitch] = useState(null);
    const [showClearModal, setShowClearModal] = useState(false);

    const cloneForm = (value) => JSON.parse(JSON.stringify(value || {}));
    const intentLabel = (value) => INTENTS.find(opt => opt.value === value)?.label || value;
    const categoryLabel = (value) => CATEGORIES.find(opt => opt.value === value)?.label || value;

    useEffect(() => {
        const initialIntent = lead?.ui?.activeIntent || lead?.requirements?.intent || 'buy';
        const initialCategory = lead?.ui?.activeCategory || lead?.requirements?.category || 'residential';
        const contexts = {};
        const storedContexts = lead?.requirementsByContext || lead?.requirementsContext || {};
        Object.entries(storedContexts || {}).forEach(([intent, categories]) => {
            Object.entries(categories || {}).forEach(([category, data]) => {
                contexts[contextKey(intent, category)] = normalizeData(data);
            });
        });
        if (!Object.keys(contexts).length && lead?.requirements) {
            contexts[contextKey(initialIntent, initialCategory)] = normalizeData(lead.requirements);
        }
        contextsRef.current = contexts;
        savedContextsRef.current = cloneForm(contexts);
        const seed = contextsRef.current[contextKey(initialIntent, initialCategory)] || normalizeData({
            intent: initialIntent,
            category: initialCategory,
        });
        setForm(seed);
        setIsDirty(false);
    }, [lead?.id]);

    const handleFormChange = (section, field, value) => {
        setForm(prev => {
            setIsDirty(true);
            let next;
            if (section === null) {
                next = { ...prev, [field]: value };
            } else if (section === 'notes') {
                next = { ...prev, notes: value };
            } else {
                next = {
                    ...prev,
                    [section]: {
                        ...prev[section],
                        [field]: value
                    }
                };
            }
            contextsRef.current[contextKey(next.intent, next.category)] = next;
            return next;
        });
    };

    const handleIntentChange = (newIntent) => {
        if (newIntent === form.intent) return;
        if (isDirty) {
            setPendingSwitch({ intent: newIntent, category: form.category, type: 'intent' });
            setShowResetModal(true);
            return;
        }
        switchContext({
            nextIntent: newIntent,
            nextCategory: form.category,
            sourceForm: form,
        });
    };

    const buildCarryOver = (sourceForm, targetIntent, targetCategory) => ({
        intent: targetIntent,
        listingType: targetIntent === 'sell' ? 'sell' : 'sell',
        sellerListingIntent: targetIntent === 'sell' ? (sourceForm.sellerListingIntent || 'sale') : sourceForm.sellerListingIntent || 'unknown',
        wantedIntent: targetIntent === 'buy' ? 'buy' : targetIntent === 'rent' ? 'rent' : targetIntent === 'invest' ? 'invest' : 'unknown',
        category: targetCategory,
        basics: {
            city: sourceForm.basics?.city || '',
            localities: sourceForm.basics?.localities || '',
            propertyTypes: sourceForm.basics?.propertyTypes || [],
            pincode: sourceForm.basics?.pincode || '',
            address: sourceForm.basics?.address || '',
        },
        budget: {
            min: sourceForm.budget?.min,
            max: sourceForm.budget?.max,
        },
        specifications: {
            areaMin: sourceForm.specifications?.areaMin,
            areaMax: sourceForm.specifications?.areaMax,
            areaUnit: sourceForm.specifications?.areaUnit,
            bhkMin: sourceForm.specifications?.bhkMin,
            bhkMax: sourceForm.specifications?.bhkMax,
            possessionTimeline: sourceForm.specifications?.possessionTimeline,
            furnishing: sourceForm.specifications?.furnishing,
            moveInDate: sourceForm.specifications?.moveInDate,
        },
        advanced: {
            financing: sourceForm.advanced?.financing,
            purpose: sourceForm.advanced?.purpose,
        },
        notes: sourceForm.notes || '',
    });

    const switchContext = ({ nextIntent, nextCategory, sourceForm }) => {
        const currentKey = contextKey(form.intent, form.category);
        contextsRef.current[currentKey] = sourceForm;
        const targetKey = contextKey(nextIntent, nextCategory);
        const existing = contextsRef.current[targetKey];
        const nextForm = existing || normalizeData(buildCarryOver(sourceForm, nextIntent, nextCategory));
        setForm(nextForm);
        const savedForTarget = savedContextsRef.current[targetKey];
        const isSameAsSaved = savedForTarget ? JSON.stringify(nextForm) === JSON.stringify(savedForTarget) : false;
        setIsDirty(!isSameAsSaved);
        onContextChange?.({ intent: nextIntent, category: nextCategory, requirements: buildFlatMix(nextForm) });
    };

    const handleSaveAndSwitch = async () => {
        if (!pendingSwitch) return;
        const didSave = await handleManualSave();
        if (!didSave) return;
        switchContext({
            nextIntent: pendingSwitch.intent,
            nextCategory: pendingSwitch.category,
            sourceForm: form,
        });
        setShowResetModal(false);
        setPendingSwitch(null);
    };

    const handleLeaveWithoutSaving = () => {
        if (!pendingSwitch) return;
        const currentKey = contextKey(form.intent, form.category);
        const savedForm = savedContextsRef.current[currentKey] || normalizeData({
            intent: form.intent,
            category: form.category,
        });
        switchContext({
            nextIntent: pendingSwitch.intent,
            nextCategory: pendingSwitch.category,
            sourceForm: savedForm,
        });
        setShowResetModal(false);
        setPendingSwitch(null);
    };

    const handleCategoryChange = (val) => {
        if (val === form.category) return;
        if (isDirty) {
            setPendingSwitch({ intent: form.intent, category: val, type: 'category' });
            setShowResetModal(true);
            return;
        }
        switchContext({
            nextIntent: form.intent,
            nextCategory: val,
            sourceForm: form,
        });
    };

    const handleManualSave = async () => {
        setSaveStatus('saving');
        try {
            const flatMix = buildFlatMix(form);
            contextsRef.current[contextKey(form.intent, form.category)] = form;
            const requirementsByContext = buildContextMap(contextsRef.current);
            await onSave({
                requirements: flatMix,
                requirementsByContext,
                ui: {
                    activeIntent: form.intent,
                    activeCategory: form.category,
                },
            });
            setSaveStatus('saved');
            setIsDirty(false);
            savedContextsRef.current[contextKey(form.intent, form.category)] = cloneForm(form);

            setTimeout(() => setSaveStatus('idle'), 2000);
            return true;
        } catch (err) {
            console.error("Save failed", err);
            setSaveStatus('error');
            return false;
        }
    };

    useImperativeHandle(ref, () => ({
        save: handleManualSave,
        isDirty,
    }), [isDirty]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
        if (!form) return;
        onDraftChange?.({
            intent: form.intent,
            category: form.category,
            requirements: buildFlatMix(form),
        });
    }, [form, onDraftChange]);

    const renderForm = () => {
        const props = { data: form, onChange: handleFormChange };
        switch (form.intent) {
            case 'buy': return <IntentBuyForm {...props} />;
            case 'rent': return <IntentRentForm {...props} />;
            case 'sell': return <IntentSellForm {...props} />;
            case 'invest': return <IntentInvestForm {...props} />;
            default: return <IntentOtherForm {...props} />;
        }
    };

    return (
        <div className="req-container">
            {/* Top Controls */}
            <div className="req-segment-group">
                <div className="req-segment">
                    {INTENTS.map(opt => (
                        <button
                            key={opt.value}
                            className={`req-segment-btn ${form.intent === opt.value ? 'active' : ''}`}
                            onClick={() => handleIntentChange(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <div className="req-segment" style={{ alignSelf: 'flex-start' }}>
                    {CATEGORIES.map(opt => (
                        <button
                            key={opt.value}
                            className={`req-segment-btn ${form.category === opt.value ? 'active' : ''}`}
                            onClick={() => handleCategoryChange(opt.value)}
                            style={{ padding: '8px 24px', fontSize: '13px' }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Form Area */}
            {renderForm()}

            {/* Sticky Footer */}
            <div className="req-footer">
                <div className="req-status">
                    {isDirty && <span>● Unsaved changes</span>}
                    {saveStatus === 'saved' && <span style={{ color: 'var(--crm-success)' }}>✓ Saved successfully</span>}
                    {saveStatus === 'error' && <span style={{ color: 'var(--crm-danger)' }}>⚠ Save failed</span>}
                </div>
                <div className="req-actions">
                    <button
                        className="req-btn req-btn-secondary"
                        onClick={() => setShowClearModal(true)}
                    >
                        Clear
                    </button>
                    <button
                        className="req-btn req-btn-primary"
                        onClick={handleManualSave}
                        disabled={loading || saveStatus === 'saving' || !isDirty}
                    >
                        {loading || saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            <ResetWarningModal
                isOpen={showResetModal}
                onClose={() => {
                    setShowResetModal(false);
                    setPendingSwitch(null);
                }}
                onSaveAndLeave={handleSaveAndSwitch}
                onLeaveWithoutSaving={handleLeaveWithoutSaving}
                currentLabel={
                    pendingSwitch?.type === 'category'
                        ? categoryLabel(form.category)
                        : intentLabel(form.intent)
                }
                nextLabel={
                    pendingSwitch?.type === 'category'
                        ? categoryLabel(pendingSwitch?.category)
                        : intentLabel(pendingSwitch?.intent)
                }
            />
            <ConfirmModal
                isOpen={showClearModal}
                onClose={() => setShowClearModal(false)}
                onConfirm={() => {
                    setForm(prev => ({
                        ...prev,
                        basics: { ...prev.basics, city: '', localities: '', propertyTypes: [] },
                        budget: {},
                        specifications: {},
                        advanced: {},
                    }));
                    setIsDirty(true);
                    setShowClearModal(false);
                }}
                onCancel={() => setShowClearModal(false)}
                title="Clear Requirements?"
                message="This will clear the current form fields. You can’t undo this."
                confirmText="Clear"
                cancelText="Cancel"
            />
        </div>
    );
});

export default LeadRequirementsForm;
